import { useCallback, useEffect, useState } from "react";
import { api, apiUpload } from "../../api";

/*
 * ONE COMPANY'S ROOM, SHARED ACROSS THE RECORD. The panel in the rail, the
 * "Add to room" on a timeline email, the strip on the pricing card and the
 * agreement card all show the same room, so they read it from here: one fetch
 * per company, and every write's answer (the server returns the whole panel)
 * goes to all of them at once. Nothing is cached past the page.
 */
const cache = new Map(); // companyId -> { data, at }
const listeners = new Map(); // companyId -> Set<fn>
const inflight = new Map();

function publish(companyId, data) {
  cache.set(companyId, { data });
  for (const fn of listeners.get(companyId) || []) fn(data);
}

async function load(companyId) {
  if (inflight.has(companyId)) return inflight.get(companyId);
  const p = api(`/companies/${companyId}/room`)
    .then((data) => {
      publish(companyId, data);
      return data;
    })
    .finally(() => inflight.delete(companyId));
  inflight.set(companyId, p);
  return p;
}

/** Every write returns the panel; this publishes it. */
async function write(companyId, path, method = "POST", body) {
  const data = await api(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });
  if (data && data.room !== undefined) publish(companyId, data);
  return data;
}

export const roomsApi = {
  load,
  create: (companyId) => write(companyId, `/companies/${companyId}/room`, "POST"),
  update: (companyId, patch) => write(companyId, `/companies/${companyId}/room`, "PATCH", patch),
  reissue: (companyId) => write(companyId, `/companies/${companyId}/room/reissue`, "POST"),
  preview: (companyId) => api(`/companies/${companyId}/room/preview`),
  add: (companyId, body) => write(companyId, `/companies/${companyId}/room/items`, "POST", body),
  order: (companyId, section, ids) => write(companyId, `/companies/${companyId}/room/order`, "POST", { section, ids }),
  editItem: (companyId, itemId, body) => write(companyId, `/rooms/items/${itemId}`, "PATCH", body),
  removeItem: (companyId, itemId) => write(companyId, `/rooms/items/${itemId}`, "DELETE"),
  recapDraft: (companyId, activityId) => api(`/companies/${companyId}/room/recap-draft?activity_id=${encodeURIComponent(activityId)}`),
  pricingOptions: (companyId) => api(`/companies/${companyId}/room/pricing-options`),
  library: (archived = false) => api(`/room-library${archived ? "?archived=1" : ""}`),
  addLink: (body) => api("/room-library", { method: "POST", body: JSON.stringify(body) }),
  editLibrary: (id, body) => api(`/room-library/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  archive: (id, archived = true) => api(`/room-library/${id}/archive`, { method: "POST", body: JSON.stringify({ archived }) }),
  upload: (file) => apiUploadNamed("/room-library/files", file),
  list: () => api("/rooms"),
  // Their logo on the page (migration 071).
  uploadLogo: (companyId, file) => uploadThenPublish(companyId, `/companies/${companyId}/room/logo`, file),
  removeLogo: (companyId) => write(companyId, `/companies/${companyId}/room/logo`, "DELETE"),
  findLogo: (companyId) => write(companyId, `/companies/${companyId}/room/logo/find`, "POST"),
  // A person's own link (migration 072): → { person: { url, name } } and the panel.
  // Next steps (migration 073).
  addStep: (companyId, body) => write(companyId, `/companies/${companyId}/room/steps`, "POST", body),
  standardSteps: (companyId) => write(companyId, `/companies/${companyId}/room/steps`, "POST", { standard: true }),
  editStep: (companyId, stepId, body) => write(companyId, `/rooms/steps/${stepId}`, "PATCH", body),
  removeStep: (companyId, stepId) => write(companyId, `/rooms/steps/${stepId}`, "DELETE"),
  orderSteps: (companyId, ids) => write(companyId, `/companies/${companyId}/room/steps/order`, "POST", { ids }),
  personLink: (companyId, contactId) => write(companyId, `/companies/${companyId}/room/people`, "POST", { contact_id: contactId }),
};

async function uploadThenPublish(companyId, path, file) {
  const data = await apiUpload(path, file);
  if (data && data.room !== undefined) publish(companyId, data);
  return data;
}

// apiUpload with the file's name in a header, so the room can offer it under its own name.
async function apiUploadNamed(path, file) {
  const token = localStorage.getItem("dashboard_token");
  const res = await fetch(`/api${path}`, {
    method: "POST",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      "X-Filename": encodeURIComponent(file.name || "file"),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: file,
  });
  if (res.status === 401) return apiUpload(path, file); // let the shared helper send them to /login
  const body = await res.json().catch(() => ({ error: "Upload failed" }));
  if (!res.ok) throw new Error(body.error || "Upload failed");
  return body;
}

/**
 * The room for `companyId`: { data, loading, error, reload }. data.room is null
 * while the company has none.
 */
export default function useRoom(companyId) {
  const [data, setData] = useState(() => (companyId && cache.get(companyId) ? cache.get(companyId).data : null));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) return undefined;
    if (!listeners.has(companyId)) listeners.set(companyId, new Set());
    const set = listeners.get(companyId);
    set.add(setData);
    if (!cache.has(companyId)) load(companyId).catch((e) => setError(e.message));
    else setData(cache.get(companyId).data);
    return () => set.delete(setData);
  }, [companyId]);

  const reload = useCallback(() => (companyId ? load(companyId).catch((e) => setError(e.message)) : null), [companyId]);
  return { data, loading: !data && !error, error, reload };
}
